import { I18n } from '@boschebike/ebds-js';
import React from 'react';
import { getContent, DEFAULT_CONTENT_LANG } from '../i18n/content';
import { PlatformContext } from './platformContext';

interface LocalizationState {
  country: string;
  language: string;
  locale: string;
  colang: string;
  isEnEnLocale: boolean;
  content: I18n;
  isLoaded: boolean;
}

interface LocalizationContextProps extends LocalizationState {
}

interface LocalizationContextProviderProps {
}

/**
 * A list of all the possible Locales
 * If a locale is not on this list it
 * will not be recognized.
 * Only used in getLocale().
 */
const supportedLocales = [
  'de-AT', // "Austria"
  'de-BE',
  'de-CH', // "Switzerland"
  'de-DE', // "Germany"
  'de-LU',
  'en-AU', // "Australia"
  'en-NZ', // "New Zealand"
  'en-CA', // "Canada"
  'en-DE', // "Germany-English" - DEPRECATED
  'en-EN', // "Rest of Europe and African Countries"
  'en-GB', // "United Kingdom" - DEFAULT
  'en-US', // "United States"
  'en-TW',
  'es-ES', // "Spain"
  'fr-BE',
  'fr-CA',
  'fr-CH',
  'fr-FR', // "France"
  'fr-LU', // "Luxembourg"
  'it-CH',
  'it-IT', // "Italy"
  'ja-JP', // "Japan"
  'ko-KR', // "South Korea"
  'nl-BE', // "Belgium"
  'nl-NL', // "Netherlands"
  'zh-TW', // "Taiwan"
  'cs-CZ', // "Czech Republic"
  'da-DK', // "Denmark"
  'fi-FI', // "Finland"
  'no-NO', // "Norway"
  'pl-PL', // "Poland"
  'pt-PT', // "Portugal"
  'sv-SE', // "Sweden"
];

const defaultState: LocalizationState = {
  country: '',
  language: '',
  locale: '',
  colang: '',
  isEnEnLocale: false,
  content: { t: () => '' }, // TODO: update components to use this content
  isLoaded: false,
};

const validateSupportedLocale = (locale: string): string => (
  supportedLocales.indexOf(locale) < 0 ? DEFAULT_CONTENT_LANG : locale
);

const getLocaleFromHTML = (): string => {
  const firstHTMLTag = document.getElementsByTagName('html')[0];
  return firstHTMLTag.getAttribute('lang') ?? '';
};

const getCountry = window.getCountryFromUrl || (() => 'de');

const getLanguage = window.getLanguageFromURL || (() => 'en');

export const LocalizationContext = React.createContext<LocalizationContextProps>(defaultState);

export const LocalizationContextProvider: React.FunctionComponent<React.PropsWithChildren<LocalizationContextProviderProps>> = ({ children }) => {
  const htmlLocale = getLocaleFromHTML();
  const platformContext = React.useContext(PlatformContext);
  const [localizationState, setLocalizationState] = React.useState<LocalizationState>(defaultState);

  const updateLocalizationState = async () => {
    if (!platformContext.isLoaded) {
      return;
    }

    const splittedHtmlLocale = htmlLocale.split('-');
    let currentCountry = splittedHtmlLocale[1];
    let currentLanguage = splittedHtmlLocale[0];
    let currentLocale = htmlLocale;
    let currentIsEnEnLocale = false;

    if (platformContext.isDXF) {
      currentCountry = getCountry();
      currentLanguage = getLanguage();
      currentLocale = `${currentLanguage}-${currentCountry.toUpperCase()}`;
      currentIsEnEnLocale = `${currentLanguage}-${currentCountry}`.includes('en-en');
    }

    const locale = validateSupportedLocale(currentLocale);
    const content = await getContent(locale);
    setLocalizationState({
      ...localizationState,
      country: currentCountry,
      language: currentLanguage,
      locale,
      colang: platformContext.isDXF ? `/${currentCountry}/${currentLanguage}` : '',
      isEnEnLocale: currentIsEnEnLocale,
      content,
      isLoaded: true,
    });
  };

  React.useEffect(() => {
    updateLocalizationState();
  }, [platformContext]);

  return (
    <LocalizationContext.Provider value={localizationState}>
      {children}
    </LocalizationContext.Provider>
  );
};
